import { ChevronDown, ChevronRight } from 'lucide-react';
import { _t } from '@/i18n/t';

import SidebarItem from './SidebarItem';
import SidebarSubsection from './SidebarSubsection';
import { isSelectedPath } from './data';
import type { MenuGroup } from './types';

type Props = {
  group: MenuGroup;
  isCollapsed: boolean;
  isOpen: boolean;
  onToggle: () => void;
  pathname: string;
};

const SidebarSection = ({ group, isCollapsed, isOpen, onToggle, pathname }: Props) => {
  const subgroups = group.subgroups ?? [];
  const hasContent = group.items.length > 0 || subgroups.length > 0;
  const canCollapse = !!group.collapsible && !isCollapsed;
  const groupLabel = _t(group.labelKey);
  const toggleLabel = isOpen ? _t('sidebar.section.collapse') : _t('sidebar.section.expand');

  return (
    <div className="mt-3">
      {!isCollapsed && canCollapse ? (
        <button
          onClick={onToggle}
          className="group flex w-full cursor-pointer items-center justify-between bg-transparent px-4 pb-2 hover:bg-transparent"
          title={`${toggleLabel} ${groupLabel}`}
          aria-expanded={isOpen}
        >
          <span className="text-nav-heading text-sidebar-heading uppercase">{groupLabel}</span>
          {isOpen ? (
            <ChevronDown className="h-3 w-3 text-nav-heading opacity-0 group-hover:opacity-100 transition-opacity" />
          ) : (
            <ChevronRight className="h-3 w-3 text-nav-heading opacity-0 group-hover:opacity-100 transition-opacity" />
          )}
        </button>
      ) : (
        !isCollapsed && (
          <div className="px-4 pb-2">
            <span className="text-nav-heading text-sidebar-heading uppercase">{groupLabel}</span>
          </div>
        )
      )}

      {hasContent && (
        <div
          className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out ${
            isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
          }`}
        >
          <div className="min-h-0">
            <div
              className={`transition-all duration-300 ease-in-out ${
                isOpen ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'
              }`}
            >
              {group.items.length > 0 && (
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <SidebarItem
                      key={item.id}
                      item={item}
                      isCollapsed={isCollapsed}
                      selected={isSelectedPath(pathname, item.relatedPaths)}
                    />
                  ))}
                </div>
              )}

              {subgroups.map((subgroup) => (
                <SidebarSubsection
                  key={subgroup.id}
                  subgroup={subgroup}
                  isCollapsed={isCollapsed}
                  pathname={pathname}
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SidebarSection;
